import { useTranslation } from 'react-i18next'
import { Field, Form, useFormState } from 'react-final-form'
import {
  InputField,
  SelectField,
  ImageField,
  DateField,
  CheckboxField,
  TextareaField,
  TimeField,
} from 'components/Inputs/Inputs'
import { useToast, RadioGroup, Radio, Stack } from '@chakra-ui/react'
import { useState, useRef, Fragment, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query/react'
import addImage from 'assets/images/addImage.svg'
import { emailRegex, passwordRegex } from 'redux/_helpers/Constants'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import arrayMutators from 'final-form-arrays'
import closeImage from 'assets/images/close.png'

import {
  useAddClientAPIMutation,
  useAddPackageMutation,
  useEditClientAPIMutation,
  useGetAllTransportationsQuery,
  useGetClientByIdQuery,
  useGetConfigurationQuery,
  useGetHotelGeneralConfigrationQuery,
  useGetPackageByIdQuery,
  useGetRoomsTypesQuery,
  useGetTransportationByIdQuery,
  useUpdatePackageMutation,
} from 'redux/store/actions'
import { FieldArray } from 'react-final-form-arrays'
import { Table } from 'components/TableComponents/Table'
import { TableActions } from 'components/TableActions/TableActions'
import { HotelDetailsArr } from '../fieldArray/HotelDetailsArr'
import { TransportationCompanyField } from '../fieldArray/TransportationCompanyField'
import { FlightCompanyField } from '../fieldArray/FlightCompanyField'

dayjs.extend(isSameOrBefore)

interface IProps {
  mode?: string
}

export default function PackagesForm({ mode }: IProps): JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const params = useParams()
  const personalImageRef = useRef<HTMLInputElement>(null)
  const [startDate, setStartDate] = useState<string | number>()
  const [endDate, setEndDate] = useState<string | number>()
  const [datesArray, setDatesArray] = useState<string[]>([])
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [choosenCompanes, setChoosenCompanes] = useState<any>()
  const [choosenFlights, setChoosenFlights] = useState<any>()
  const [deletedHotelItems, setDeletedHotelItems] = useState<any>([])
  const [deletedFlights, setDeletedFlights] = useState<any>([])
  const [deletedTransportations, setDeletedTransportations] = useState<any>([])
  const [deletedActivities, setDeletedActivities] = useState<any>()
  const [addPackage] = useAddPackageMutation()
  const [updatePackage] = useUpdatePackageMutation()
  const { data: packageDataById } = useGetPackageByIdQuery(params.id || skipToken)

  const generateDatesArray = (start: string, end: string) => {
    const dates: string[] = []
    let currentDate: any = dayjs(start)
    const lastDate = dayjs(end)
    while (currentDate.isSameOrBefore(lastDate)) {
      dates.push(currentDate.format('dddd, DD/MM/YYYY'))
      currentDate = currentDate.add(1, 'day')
    }
    return dates
  }
  useEffect(() => {
    if (startDate && endDate) {
      const dates = generateDatesArray(startDate.toString(), endDate.toString())
      setDatesArray(dates)
    } else {
      setDatesArray([])
    }
  }, [startDate, endDate])
  useEffect(() => {
    if (packageDataById) {
      setStartDate(packageDataById.Items.StartDate)
      setEndDate(packageDataById.Items.EndDate)
    }
  }, [packageDataById])
  const { data: configrations } = useGetHotelGeneralConfigrationQuery({})
  const formatPackageAgenda = (formData: any) => {
    const activityKeys = Object.keys(formData).filter(key => key.startsWith('activityDetails-'))
    const packageAgendas = activityKeys.map(key => {
      const dateStr = key.split('-')[1]
      const [day, month, year] = dateStr.split('/')
      const formattedDate = `${year}-${month}-${day}`
      return {
        activityDate: formattedDate,
        activityDetails: formData[key].map((activity: any) => ({
          ActivityTime: activity.ActivityTime || '',
          Description: activity.Description || '',
          DescriptionAr: activity.DescriptionAr || '',
          isDeleted: false,
        })),
      }
    })
    return {
      ...formData,
      packageAgendas,
    }
  }
  const onSubmit = (values: any) => {
    const formattedAgenda = formatPackageAgenda(values)
    const newFormatedData: any = {}
    Object.keys(formattedAgenda).forEach(key => {
      if (!key.startsWith('activityDetails')) {
        newFormatedData[key] = formattedAgenda[key]
      }
    })

    newFormatedData.packageFlights = choosenFlights
    newFormatedData.packageTransportations = choosenCompanes
    newFormatedData.packageHotels = formattedAgenda.packageHotels.map((hotel: any) => {
      const { id, ...hotelWithoutId } = hotel
      return hotelWithoutId
    })

    if (mode === 'edit') {
      const allValues = [...newFormatedData.packageHotels, ...deletedHotelItems]

      const uniqueFlights = newFormatedData.packageFlights.filter(
        (flight: any, index: number, self: any[]) =>
          self.findIndex((f: any) => f.FlightTravelId === flight.FlightTravelId) === index
      )

      newFormatedData.packageFlights = uniqueFlights.map((flight: any, index: number) => ({
        ...flight,
        index,
      }))
      const uniqueTransportations = newFormatedData.packageTransportations.filter(
        (transportation: any, index: number, self: any[]) =>
          self.findIndex(
            (f: any) => f.TransportationTravelId === transportation.TransportationTravelId
          ) === index
      )

      newFormatedData.packageTransportations = uniqueTransportations.map(
        (transportation: any, index: number) => ({
          ...transportation,
          index,
        })
      )
      newFormatedData.packageFlights = [...newFormatedData.packageFlights, ...deletedFlights]
      newFormatedData.packageTransportations = [
        ...newFormatedData.packageTransportations,
        ...deletedTransportations,
      ]
      newFormatedData.packageHotels = allValues
      updatePackage({ body: newFormatedData, id: params.id }).then((res: any) => {
        if (res.data.Message === 'Package Updated Successfullt') {
          toast({
            title: 'Package updated successfully',
            status: 'success',
            duration: 1500,
            isClosable: true,
          })
          navigate(`/admin/packages/`)
        }
      })
    } else {
      addPackage(newFormatedData)
        .then((res: any) => {
          if (res.data.Message === 'Package Has Been Created Successfully') {
            toast({
              title: t('Package created successfully'),
              status: 'success',
              duration: 1500,
              isClosable: true,
            })
            navigate(`/admin/packages/`)
          }
        })
        .catch((err: any) => {
          toast({
            title: err.data.Message,
            status: 'error',
            duration: 1500,
            isClosable: true,
          })
        })
    }
  }
  const reverseFormatPackageAgenda = (apiData: any) => {
    if (!apiData) return null
    if (!apiData?.packageAgendas) return apiData
    const formData = { ...apiData }
    apiData.packageAgendas.forEach((agenda: any) => {
      const date = dayjs(agenda.ActivityDate)
      const formattedDate = date.format('DD/MM/YYYY')
      const fieldName = `activityDetails-${formattedDate}`

      formData[fieldName] = agenda.ActivityDetails.map((activity: any) => ({
        ActivityTime: activity.ActivityTime,
        Description: activity.Description,
        DescriptionAr: activity.DescriptionAr,
        Id: activity.Id,
      }))
    })
    delete formData.packageAgendas
    return formData
  }
  const [newData, setNewData] = useState<any>(null)
  useEffect(() => {
    if (packageDataById) {
      setNewData(reverseFormatPackageAgenda(packageDataById?.Items))
      setChoosenFlights(packageDataById?.Items?.packageFlights)
      setChoosenCompanes(packageDataById?.Items?.packageTransportations)
    }
  }, [packageDataById])
  const validate = (values: any): any => {
    const errors: any = {}
    if (!values.Name) {
      errors.Name = t('validation required')
    }
    if (!values.PackageTypeId) {
      errors.PackageTypeId = t('validation required')
    }
    if (!values.Class) {
      errors.Class = t('validation required')
    }
    if (!values.Capacity) {
      errors.Capacity = t('validation required')
    }
    if (!values.StartDate) {
      errors.StartDate = t('validation required')
    }
    if (!values.EndDate) {
      errors.EndDate = t('validation required')
    }
    if (!values.BookingCloseDate) {
      errors.BookingCloseDate = t('validation required')
    }
    if (!values.Price) {
      errors.Price = t('validation required')
    }
    if (!values.CurrencyId) {
      errors.CurrencyId = t('validation required')
    }
    if (!values.OriginCountryId) {
      errors.OriginCountryId = t('validation required')
    }
    if (!values.DestinationCountryId) {
      errors.DestinationCountryId = t('validation required')
    }
    if (!values.Notes) {
      errors.Notes = t('validation required')
    }
    if (!values.TermsConditions) {
      errors.TermsConditions = t('validation required')
    }
    if (values.packageHotels && values.packageHotels.length > 0) {
      const hotelErrors: any[] = []
      values.packageHotels.forEach((hotel: any, index: number) => {
        const hotelError: any = {}
        if (!hotel.HotelId) {
          hotelError.HotelId = t('validation required')
        }

        if (!hotel.CheckInDate) {
          hotelError.CheckInDate = t('validation required')
        }
        if (!hotel.HotelRoomId) {
          hotelError.HotelRoomId = t('validation required')
        }
        if (!hotel.CheckOutDate) {
          hotelError.CheckOutDate = t('validation required')
        }
        if (!hotel.CountryId) {
          hotelError.CountryId = t('validation required')
        }
        if (!hotel.CityId) {
          hotelError.CityId = t('validation required')
        }
        if (Object.keys(hotelError).length > 0) {
          hotelErrors[index] = hotelError
        }
      })
      if (hotelErrors.length > 0) {
        errors.packageHotels = hotelErrors
      }
    }
    return errors
  }
  return (
    <div className="">
      <Form
        onSubmit={onSubmit}
        validate={validate}
        mutators={{ ...arrayMutators }}
        initialValues={newData || {}}
        render={({ handleSubmit, form, submitting, pristine }) => {
          return (
            <form onSubmit={handleSubmit} className="">
              <div className="white-container mt-4 rounded-xl">
                <p className="text-base text-black font-bold ">{t('Package Details')}</p>
                <div className="  ">
                  <div className="w-full flex flex-wrap ">
                    <div className="w-full flex flex-wrap">
                      <div className="w-1/4  p-1 pt-4 ">
                        <Field
                          label="Inactive"
                          name="IsInActive"
                          disabled={mode === 'view'}
                          render={CheckboxField}
                        />
                      </div>
                      <div className="w-3/4" />
                      <div className="w-1/5 p-1">
                        <Field
                          name="PackageImage"
                          type="file"
                          disabled={mode === 'view'}
                          className="h-[155px]"
                          label={t('Upload Personal Photo')}
                          labelChange={t('Change Personal Photo')}
                          render={props => <ImageField {...props} ref={personalImageRef} />}
                        />
                      </div>
                      <div className=" flex flex-wrap w-4/5 p-1">
                        <div className="w-1/4 p-1">
                          <Field
                            label="Package Name"
                            name="Name"
                            type="text"
                            render={InputField}
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Package Name Ar"
                            name="NameAr"
                            type="text"
                            render={InputField}
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Package Type"
                            name="PackageTypeId"
                            render={SelectField}
                            options={configrations?.data?.PackageTypes || []}
                            valueField="Id"
                            textField="Value"
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Class"
                            name="Class"
                            render={SelectField}
                            options={[
                              { Id: 'Economy', Value: t('Economy'), ValueAr: 'اقتصادي' },
                              { Id: '3 stars', Value: t('3 stars'), ValueAr: '3 نجوم' },
                              { Id: '4 stars', Value: t('4 stars'), ValueAr: '4 نجوم' },
                              { Id: '5 stars', Value: t('5 stars'), ValueAr: '5 نجوم' },
                            ]}
                            valueField="Id"
                            textField="Value"
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Capacity"
                            name="Capacity"
                            type="number"
                            render={InputField}
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Start Date"
                            name="StartDate"
                            render={props => (
                              <DateField
                                {...props}
                                onChange={(value: string) => {
                                  props.input.onChange(value)
                                  setStartDate(value)
                                }}
                                disabled={!!mode}
                              />
                            )}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="End Date"
                            name="EndDate"
                            render={props => (
                              <DateField
                                {...props}
                                onChange={(value: string) => {
                                  props.input.onChange(value)
                                  setEndDate(value)
                                }}
                                min={startDate || undefined}
                                disabled={!!mode}
                              />
                            )}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Booking Closing Date"
                            name="BookingCloseDate"
                            max={startDate}
                            type="text"
                            render={DateField}
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Price"
                            name="Price"
                            type="number"
                            render={InputField}
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Currency"
                            name="CurrencyId"
                            render={SelectField}
                            options={configrations?.data?.Currencies || []}
                            valueField="Id"
                            textField="Value"
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Origin Country"
                            name="OriginCountryId"
                            render={SelectField}
                            options={configrations?.data?.Countries || []}
                            valueField="Id"
                            textField="Value"
                            disabled={mode === 'view'}
                          />
                        </div>
                        <div className="w-1/4 p-1">
                          <Field
                            label="Destination Country"
                            name="DestinationCountryId"
                            render={SelectField}
                            options={configrations?.data?.Countries || []}
                            valueField="Id"
                            textField="Value"
                            disabled={mode === 'view'}
                          />
                        </div>
                      </div>
                      <div className="w-1/2 p-1">
                        <Field
                          label="Notes"
                          name="Notes"
                          type="text"
                          render={TextareaField}
                          disabled={mode === 'view'}
                        />
                      </div>
                      <div className="w-1/2 p-1">
                        <Field
                          label="Terms & Conditions"
                          name="TermsConditions"
                          type="text"
                          render={TextareaField}
                          disabled={mode === 'view'}
                        />
                      </div>
                      <div className="w-1/2 p-1">
                        <Field
                          label="Notes Ar"
                          name="NotesAr"
                          type="text"
                          render={TextareaField}
                          disabled={mode === 'view'}
                        />
                      </div>
                      <div className="w-1/2 p-1">
                        <Field
                          label="Terms & Conditions Ar"
                          name="TermsConditionsAr"
                          type="text"
                          render={TextareaField}
                          disabled={mode === 'view'}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="white-container mt-4 pb-10 mb-3 w-full rounded-xl ">
                <p className="text-base  text-black font-bold">{t('Hotel Details')}</p>
                <div className=" w-full ">
                  <div className="w-full flex flex-wrap pb-7">
                    <div className="w-full flex flex-wrap">
                      <FieldArray className="w-full" name="packageHotels">
                        {({ fields }) => (
                          <>
                            <div className="w-full">
                              {fields.map((field, index) => (
                                <HotelDetailsArr
                                  key={fields.value[index].id}
                                  startDate={startDate}
                                  endDate={endDate}
                                  fields={fields}
                                  packageDataById={packageDataById || null}
                                  index={index}
                                  name={`${field}`}
                                  mode={mode}
                                  setDeletedHotelItems={setDeletedHotelItems}
                                  configration={configrations}
                                  deletedHotelItems={deletedHotelItems}
                                />
                              ))}
                            </div>
                            {mode !== 'view' && (
                              <div className="w-full flex justify-end mt-3 me-3 pe-1">
                                <button
                                  onClick={() => fields.push({ id: Date.now() })}
                                  className="btn-alternative text-sm   w-52 cursor-pointer ms-auto"
                                  type="button">
                                  {t('+ Add Hotel')}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </div>
              </div>
              <div className="white-container mt-4 pb-10 mb-3 w-full rounded-xl ">
                <p className="text-base  text-black font-bold">{t('Flight Details')}</p>
                <div className=" w-full ">
                  <div className="w-full flex flex-wrap pb-7">
                    <div className="w-full flex flex-wrap">
                      <FieldArray className="w-full" name="packageFlights">
                        {({ fields }) => (
                          <>
                            <div className="w-full">
                              {fields.map((field, index) => (
                                <FlightCompanyField
                                  key={fields.value[index].id}
                                  setDeletedFlights={setDeletedFlights}
                                  deletedFlights={deletedFlights}
                                  mode={mode}
                                  setChoosenFlights={setChoosenFlights}
                                  choosenFlights={choosenFlights}
                                  field={field}
                                  fields={fields}
                                  index={index}
                                  packageDataById={packageDataById || null}
                                />
                              ))}
                            </div>
                            {mode !== 'view' && (
                              <div className="w-full flex justify-end mt-3 me-3 pe-1">
                                <button
                                  onClick={() => fields.push({ id: Date.now() })}
                                  className="btn-alternative text-sm w-52 cursor-pointer ms-auto"
                                  type="button">
                                  {t('+ Add Travel')}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </div>
              </div>
              <div className="white-container mt-4 pb-10 mb-3 w-full rounded-xl ">
                <p className="text-base  text-black font-bold">{t('Transportation Details')}</p>
                <div className=" w-full ">
                  <div className="w-full flex flex-wrap pb-7">
                    <div />
                    <div className="w-full flex flex-wrap">
                      <FieldArray className="w-full" name="packageTransportations">
                        {({ fields }) => (
                          <>
                            <div className="w-full">
                              {fields.map((field: any, index: any) => (
                                <div
                                  className="w-full flex flex-wrap p-1 relative pb-10"
                                  key={fields.value[index].id}>
                                  <TransportationCompanyField
                                    mode={mode}
                                    setChoosenCompanes={setChoosenCompanes}
                                    choosenCompanes={choosenCompanes}
                                    field={field}
                                    fields={fields}
                                    setDeletedTransportations={setDeletedTransportations}
                                    deletedTransportations={deletedTransportations}
                                    index={index}
                                    packageDataById={packageDataById || null}
                                  />
                                </div>
                              ))}
                            </div>
                            {mode !== 'view' && (
                              <div className="w-full flex justify-end mt-3 me-3 pe-1">
                                <button
                                  onClick={() => fields.push({ id: Date.now() })}
                                  className="btn-alternative text-sm w-52 cursor-pointer ms-auto"
                                  type="button">
                                  {t('+ Add Travel')}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full pb-10">
                <h4 className="font-bold mb-3">{t('Activities Agenda')}</h4>
                <div className="w-full flex flex-wrap">
                  <div className="w-full h-[200px] overflow-y-scroll   mb-5 pb-5 relative">
                    <FieldArray name="dateARR">
                      {({ fields }) => (
                        <div>
                          {datesArray.length >= 1 &&
                            datesArray.map((ele: string) => (
                              <div key={ele} className="flex">
                                <div className="w-11/12">
                                  <div className="w-11/12 ">
                                    <button
                                      type="button"
                                      className={` h-[38px] w-1/6 mb-2  ${
                                        selectedDate === ele
                                          ? 'btn-primary text-white'
                                          : 'btn-alternative'
                                      }`}
                                      onClick={(e: any) => {
                                        setSelectedDate(ele)
                                        e.target.closest('.overflow-y-scroll')?.scrollTo({
                                          top: 0,
                                          behavior: 'smooth',
                                        })
                                      }}>
                                      {ele}
                                    </button>
                                  </div>
                                </div>
                                {selectedDate === ele && (
                                  <FieldArray
                                    className="pl-3 "
                                    name={`activityDetails-${ele.split(', ')[1]}`}>
                                    {({ fields }) => (
                                      <div className="w-5/6 pb-12 absolute  overflow-x-hidden right-0 rtl:right-auto rtl:left-0  top-0 bg-white p-3 mb-2 rounded-xl ml-3">
                                        {fields.map((name, index) => (
                                          <div
                                            className="flex w-full flex-wrap relative "
                                            key={name}>
                                            <div className="w-3/12 p-1">
                                              <Field
                                                label="Activity Time"
                                                name={`${name}.ActivityTime`}
                                                id="ActivityTime"
                                                render={TimeField}
                                                disabled={mode === 'view'}
                                                type="text"
                                              />
                                            </div>
                                            <div className="w-8/12" />
                                            <div className="w-6/12  rtl:pr-0  p-1">
                                              <Field
                                                label="Description"
                                                name={`${name}.Description`}
                                                type="text"
                                                render={InputField}
                                                disabled={mode === 'view'}
                                              />
                                            </div>
                                            <div className="w-6/12 pr-10 rtl:pr-0 rtl:pl-10 p-1">
                                              <Field
                                                label="Description Ar"
                                                name={`${name}.DescriptionAr`}
                                                type="text"
                                                render={InputField}
                                                disabled={mode === 'view'}
                                              />
                                            </div>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const haveId = fields.value[index].Id
                                                const deletedId = fields.value[index]
                                                const deletedItem = {
                                                  ...deletedId,
                                                  IsDeleted: true,
                                                }
                                                if (haveId) {
                                                  setDeletedActivities((prev: any) => [
                                                    ...prev,
                                                    deletedItem,
                                                  ])
                                                }
                                                fields.remove(index)
                                              }}
                                              className=" absolute right-0 rtl:right-auto rtl:left-0 bg-[#FCE5E5] bottom-1 text-red-600 text-lg w-8 h-8 text-center rounded-xl">
                                              <img
                                                src={closeImage}
                                                alt="close"
                                                className="m-auto"
                                              />
                                            </button>
                                          </div>
                                        ))}
                                        {mode !== 'view' && (
                                          <div className="w-full flex justify-end mt-3 me-3 pe-1">
                                            <button
                                              onClick={() => fields.push({ id: Date.now() })}
                                              className="btn-alternative text-sm w-52 cursor-pointer ms-auto"
                                              type="button">
                                              {t('+ Add Activity')}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </FieldArray>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                    </FieldArray>
                  </div>
                </div>
                {mode !== 'view' ? (
                  <div className="w-full flex mt-2 justify-end pt-2">
                    <button
                      className="btn-alternative cursor-pointer rounded-xl mx-2 "
                      type="button"
                      disabled={mode === 'edit' ? false : submitting || pristine}
                      onClick={() => {
                        form.reset()
                      }}>
                      {t('Clear All')}
                    </button>
                    <button
                      className="btn-primary  cursor-pointer rounded-xl me-2 w-24"
                      type="submit"
                      disabled={mode === 'edit' ? false : submitting || pristine}>
                      {t('Save')}
                    </button>
                  </div>
                ) : (
                  <div className="py-4" />
                )}
              </div>
            </form>
          )
        }}
      />
    </div>
  )
}
